import { addKitToCart, CartAddResult } from "./sleep-finder-cart";
import { loadFinderProducts } from "./sleep-finder-data";
import {
  fabricOptions,
  FinderResult,
  FinderSlot,
  formatWarning,
  getProducts,
  recommend,
  seasonFor,
  tempBandFor,
} from "./sleep-finder-engine";
import { isValidEmail, subscribeToKlaviyo } from "./sleep-finder-klaviyo";

const ALL_STEPS = ["age", "weight", "pref", "temp", "fabric", "style"];
const AUTO: Record<string, boolean> = { age: true, weight: true, pref: true, temp: false, fabric: true, style: false };
const GROUPS = ["hero", "layer", "extra", "next_stage"];
const GROUP_KEYS: Record<string, string> = {
  hero: "hero_group",
  layer: "layer_group",
  extra: "extras_group",
  next_stage: "next_stage_group",
};

/* The t filter escapes HTML in translations and a JSON script block keeps those entities, so decode
   them once here. Every copy string is written with textContent, never innerHTML -- EZ 20261028 */
function decodeEntities(value: string): string {
  if (value.indexOf("&") === -1) return value;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

interface OptionItem {
  value: string;
  label: string;
  sub?: string;
  swatch?: string;
}

class SleepFinder extends HTMLElement {
  private rules: any = null;
  private copy: Record<string, string> = {};
  private answers: any = {
    ageBand: null,
    ageMonths: null,
    rolling: null,
    tempF: 70,
    tempMode: null,
    dueMonth: null,
    weightLb: null,
    s3type: null,
    sleeves: null,
    fabric: null,
    colors: null,
  };
  private band: any = null;
  private steps: string[] = ALL_STEPS.slice();
  private index = 0;
  private fabricAsk = false;
  private result: FinderResult | null = null;
  private advanceTimer = 0;
  private interacted = false;
  private productsReady: Promise<void> = Promise.resolve();
  private pendingFocus: HTMLElement | null = null;
  private selectionSpoken = false;
  private advancing = false;

  connectedCallback() {
    const rulesNode = this.querySelector("[data-sleep-finder-rules]");
    const copyNode = this.querySelector("[data-sleep-finder-copy]");
    if (!rulesNode || !rulesNode.textContent || !rulesNode.textContent.trim()) return;
    try {
      this.rules = JSON.parse(rulesNode.textContent);
      this.copy = copyNode && copyNode.textContent ? JSON.parse(copyNode.textContent) : {};
      Object.keys(this.copy).forEach((key) => {
        if (typeof this.copy[key] === "string") this.copy[key] = decodeEntities(this.copy[key]);
      });
    } catch (error) {
      console.error(error);
      this.showNap();
      return;
    }

    this.addEventListener("click", this.onClick);
    this.addEventListener("input", this.onInput);
    this.addEventListener("change", this.onChange);
    this.addEventListener("submit", this.onSubmit);
    this.applyCopyPhrases();
    this.fillFromRules();
    this.go(0, false);
    this.track("finder_start", {});
    this.productsReady = loadFinderProducts(this.rules, this.dataset.collection || "")
      .then(() => undefined)
      .catch((error) => {
        console.error(error);
        this.showNap();
      });
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.removeEventListener("input", this.onInput);
    this.removeEventListener("change", this.onChange);
    this.removeEventListener("submit", this.onSubmit);
    window.clearTimeout(this.advanceTimer);
  }

  private onClick = (event: Event) => {
    this.interacted = true;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const option = target.closest(".sleep-finder__opt") as HTMLButtonElement | null;
    if (option) {
      this.onOption(option);
      return;
    }
    if (target.closest("[data-prev]")) {
      if (this.index > 0) this.go(this.index - 1, true);
      return;
    }
    if (target.closest("[data-next]")) {
      void this.onNext();
      return;
    }
    const qtyButton = target.closest("[data-inc],[data-dec]") as HTMLButtonElement | null;
    if (qtyButton) {
      this.onQty(qtyButton);
      return;
    }
    if (target.closest("[data-add]")) {
      const button = target.closest("[data-add]") as HTMLButtonElement;
      void this.add(false, button);
      return;
    }
    if (target.closest("[data-back]")) {
      this.backFromResult();
      return;
    }
    if (target.closest("[data-restart]")) window.location.reload();
  };

  private onInput = (event: Event) => {
    this.interacted = true;
    const input = event.target as HTMLInputElement | null;
    if (!input || input.dataset.field !== "tempF") return;
    this.answers.tempF = parseFloat(input.value);
    this.querySelectorAll('[data-field="tempF"]').forEach((node) => {
      if (node !== input) (node as HTMLInputElement).value = input.value;
    });
    const band = tempBandFor(this.answers.tempF);
    const label = this.querySelector("[data-temp-label]");
    if (label) {
      label.textContent = this.text(this.copy.temp_to || "", { band: band.label, tog: band.tog_primary });
    }
    // A typed value outside the field's range (or an emptied field) must not reach the engine -- EZ 20261028
    const inRange = this.tempInRange();
    this.querySelectorAll('input[type="number"][data-field="tempF"]').forEach((node) => {
      if (inRange) node.removeAttribute("aria-invalid");
      else node.setAttribute("aria-invalid", "true");
    });
    this.validate();
  };

  private tempInRange(): boolean {
    const number = this.querySelector('input[type="number"][data-field="tempF"]') as HTMLInputElement | null;
    const min = number && number.min !== "" ? parseFloat(number.min) : -Infinity;
    const max = number && number.max !== "" ? parseFloat(number.max) : Infinity;
    const value = this.answers.tempF;
    return typeof value === "number" && isFinite(value) && value >= min && value <= max;
  }

  private onChange = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target || !this.result) return;
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      const row = target.closest(".sleep-finder__item");
      if (row) row.classList.toggle("sleep-finder__item--off", !target.checked);
      this.paintTotal();
      return;
    }
    if (target instanceof HTMLSelectElement && target.dataset.size != null) {
      const index = Number(target.dataset.size);
      const line = this.result.kit[index];
      const variant = this.variantsFor(line).find((item) => item.id === target.value);
      if (!line || !variant) return;
      line.variant_id = variant.id;
      line.variant_title = variant.title;
      line.price = variant.price;
      const price = this.querySelector('[data-price="' + index + '"]');
      if (price) price.textContent = this.money(line.price * line.qty);
      const meta = target.parentElement?.querySelector(".sleep-finder__meta");
      if (meta) meta.textContent = variant.title;
      this.track("finder_size_change", { slot: line.slot, size: variant.title });
      this.paintTotal();
    }
  };

  private onSubmit = (event: Event) => {
    const form = event.target as HTMLFormElement | null;
    if (!form || !form.matches("[data-offer]")) return;
    event.preventDefault();
    void this.onOffer(form);
  };

  private fillFromRules() {
    const rules = this.rules;
    const ageOptions: OptionItem[] = rules.age_bands
      .filter((band: any) => band.show_as_button !== "no")
      .map((band: any) => ({ value: band.id, label: band.label, sub: band.sublabel }));
    for (const group of rules.age_groups || []) {
      ageOptions.push({ value: "group:" + group.id, label: group.label, sub: group.sublabel });
    }
    this.fill('[data-field="ageBand"]', ageOptions);
    if (rules.preferences) {
      this.fill(
        '[data-field="s3type"]',
        rules.preferences.type_options.map((option: any) => ({ value: option.id, label: option.label, sub: option.sublabel }))
      );
      this.fill(
        '[data-field="sleeves"]',
        rules.preferences.sleeve_options.map((option: any) => ({ value: option.id, label: option.label, sub: option.sublabel }))
      );
    }
    const colors = rules.colors || { options: [] };
    const colourOptions = (colors.options || []).map((option: any) => ({
      value: option.id,
      label: option.label,
      sub: option.sublabel,
      swatch: option.swatch,
    }));
    colourOptions.push({ value: "any", label: colors.any_label || this.copy.colors_any || "", sub: "" });
    this.fill('[data-field="colors"]', colourOptions);
    this.fillDueMonths();
  }

  private fillDueMonths() {
    const names = (this.rules.season_tog && this.rules.season_tog.month_names) || [];
    const now = new Date();
    const items: OptionItem[] = [];
    for (let index = 0; index < 10; index++) {
      const date = new Date(now.getFullYear(), now.getMonth() + index, 1);
      items.push({
        value: String(date.getMonth() + 1),
        label: String(names[date.getMonth()] || "").slice(0, 3),
        sub: String(date.getFullYear()),
      });
    }
    this.fill('[data-field="dueMonth"]', items);
  }

  private fill(selector: string, items: OptionItem[]) {
    const wrap = this.querySelector(selector);
    if (!wrap) return;
    wrap.textContent = "";
    for (const item of items) wrap.appendChild(this.optionButton(item));
  }

  private optionButton(item: OptionItem): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "sleep-finder__opt";
    button.dataset.value = item.value;
    button.setAttribute("aria-pressed", "false");
    if (item.swatch) {
      const swatch = document.createElement("span");
      swatch.className = "sleep-finder__swatch" + (item.swatch === "print" ? " sleep-finder__swatch--print" : "");
      swatch.setAttribute("aria-hidden", "true");
      if (item.swatch !== "print") swatch.style.background = item.swatch;
      button.appendChild(swatch);
    }
    button.appendChild(document.createTextNode(item.label));
    if (item.sub) {
      const small = document.createElement("small");
      small.textContent = item.sub;
      button.appendChild(small);
    }
    return button;
  }

  private onOption(option: HTMLButtonElement) {
    const wrap = option.parentElement;
    if (!wrap) return;
    const field = wrap.dataset.field || "";
    if (wrap.hasAttribute("data-multi")) {
      this.toggleMulti(wrap, option);
      this.validate();
      return;
    }
    wrap.querySelectorAll(".sleep-finder__opt").forEach((button) => {
      const selected = button === option;
      button.classList.toggle("sleep-finder__opt--selected", selected);
      button.setAttribute("aria-pressed", selected ? "true" : "false");
    });
    this.pendingFocus = null;
    if (field === "dueMonth") {
      this.answers.dueMonth = option.dataset.value;
      this.seasonLabel();
      this.validate();
      this.autoAdvance();
      this.finishSelection(option);
      return;
    }
    if (field === "tempMode") {
      this.answers.tempMode = option.dataset.value;
      const tempWrap = this.querySelector("[data-temp-wrap]") as HTMLElement | null;
      if (tempWrap) tempWrap.hidden = this.answers.tempMode !== "temp";
      if (this.answers.tempMode === "temp") {
        const range = this.querySelector('input[type="range"][data-field="tempF"]') as HTMLInputElement | null;
        if (range) {
          this.answers.tempF = parseFloat(range.value);
          range.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
      this.seasonLabel();
      this.validate();
      this.finishSelection(option);
      return;
    }
    if (field === "ageBand" || field === "ageBandExact") {
      this.onAge(field, option.dataset.value || "");
      this.finishSelection(option);
      return;
    }
    if (field === "s3type") {
      this.answers.s3type = option.dataset.value;
      const chosen = (this.rules.preferences.type_options || []).find((item: any) => item.id === this.answers.s3type);
      const askSleeves = !chosen || chosen.ask_sleeves !== "no";
      const sleeveWrap = this.querySelector("[data-sleeve-wrap]") as HTMLElement | null;
      if (sleeveWrap) sleeveWrap.hidden = !askSleeves;
      this.querySelectorAll('[data-field="sleeves"] .sleep-finder__opt').forEach((button) => {
        button.classList.remove("sleep-finder__opt--selected");
        button.setAttribute("aria-pressed", "false");
      });
      this.answers.sleeves = askSleeves ? null : "any";
      this.validate();
      if (!askSleeves) this.autoAdvance();
      this.finishSelection(option);
      return;
    }
    if (field === "rolling") {
      this.answers.rolling = option.dataset.value;
      this.fillWeights();
      this.validate();
      this.autoAdvance();
      this.finishSelection(option);
      return;
    }
    this.answers[field] = field === "weightLb" ? parseFloat(option.dataset.value || "") : option.dataset.value;
    this.validate();
    this.autoAdvance();
    this.finishSelection(option);
  }

  private toggleMulti(wrap: HTMLElement, option: HTMLButtonElement) {
    const value = option.dataset.value || "";
    if (value === "any") {
      wrap.querySelectorAll(".sleep-finder__opt").forEach((button) => {
        const selected = button === option;
        button.classList.toggle("sleep-finder__opt--selected", selected);
        button.setAttribute("aria-pressed", selected ? "true" : "false");
      });
    } else {
      const selected = option.getAttribute("aria-pressed") !== "true";
      option.classList.toggle("sleep-finder__opt--selected", selected);
      option.setAttribute("aria-pressed", selected ? "true" : "false");
      const anyButton = wrap.querySelector('[data-value="any"]');
      if (anyButton) {
        anyButton.classList.remove("sleep-finder__opt--selected");
        anyButton.setAttribute("aria-pressed", "false");
      }
    }
    const picked = Array.from(wrap.querySelectorAll(".sleep-finder__opt--selected")).map(
      (button) => (button as HTMLElement).dataset.value || ""
    );
    this.answers.colors = picked.length ? picked.filter((item) => item !== "any") : null;
    const name = this.optionName(option);
    const pressed = option.getAttribute("aria-pressed") === "true";
    const template = pressed ? this.copy.selected || "" : this.copy.removed || "";
    this.announce(this.text(template, { label: name }));
  }

  private onAge(field: string, value: string) {
    const follow = this.querySelector("[data-age-follow]") as HTMLElement | null;
    if (value.indexOf("group:") === 0) {
      const group = (this.rules.age_groups || []).find((item: any) => item.id === value.slice(6));
      if (follow) follow.hidden = false;
      const heading = this.querySelector("[data-follow-q]");
      if (heading) heading.textContent = (group && group.question) || heading.textContent;
      const items = String(group.bands || "")
        .split("|")
        .map((id) => this.rules.age_bands.find((band: any) => band.id === id))
        .filter(Boolean)
        .map((band: any) => ({ value: band.id, label: band.label, sub: band.sublabel }));
      this.fill('[data-field="ageBandExact"]', items);
      this.answers.ageBand = null;
      this.band = null;
      this.hide(this.querySelector("[data-rolling]"));
      this.hide(this.querySelector("[data-due-wrap]"));
      this.validate();
      if (follow) this.pendingFocus = follow.querySelector("h2") as HTMLElement | null;
      return;
    }
    if (field === "ageBand" && follow) follow.hidden = true;
    const band = this.rules.age_bands.find((item: any) => item.id === value);
    if (band) this.setBand(band);
    this.autoAdvance();
  }

  private setBand(band: any) {
    this.band = band;
    this.answers.ageBand = band.id;
    this.answers.ageMonths = null;
    const rolling = this.querySelector("[data-rolling]") as HTMLElement | null;
    if (rolling) rolling.hidden = band.ask_rolling !== "yes";
    const due = this.querySelector("[data-due-wrap]") as HTMLElement | null;
    if (due) {
      due.hidden = band.id !== "expecting";
      due.querySelectorAll(".sleep-finder__opt").forEach((button) => {
        button.classList.remove("sleep-finder__opt--selected");
        button.setAttribute("aria-pressed", "false");
      });
    }
    this.answers.dueMonth = null;
    this.answers.rolling = rolling && rolling.hidden ? "no" : null;
    if (rolling) {
      rolling.querySelectorAll(".sleep-finder__opt").forEach((button) => {
        button.classList.remove("sleep-finder__opt--selected");
        button.setAttribute("aria-pressed", "false");
      });
    }
    this.fabricAsk = false;
    this.answers.fabric = null;
    this.answers.weightLb = null;
    this.answers.s3type = null;
    this.answers.sleeves = null;
    this.querySelectorAll('[data-field="s3type"] .sleep-finder__opt, [data-field="sleeves"] .sleep-finder__opt').forEach((button) => {
      button.classList.remove("sleep-finder__opt--selected");
      button.setAttribute("aria-pressed", "false");
    });
    const sleeves = this.querySelector("[data-sleeve-wrap]") as HTMLElement | null;
    if (sleeves) sleeves.hidden = true;
    this.computeSteps();
    this.fillWeights();
    this.drawProgress();
    this.validate();
  }

  private fillWeights() {
    if (!this.band) return;
    const rollingKey = this.answers.rolling === "yes" ? "yes" : "no";
    const options = (this.rules.weight_options || []).filter(
      (row: any) => row.age_band === this.band.id && (!row.rolling || row.rolling === "any" || row.rolling === rollingKey)
    );
    this.fill(
      '[data-field="weightLb"]',
      options.map((row: any) => ({ value: String(row.weight_lb), label: row.label, sub: row.sublabel }))
    );
    this.answers.weightLb = null;
  }

  private computeSteps() {
    this.steps = ALL_STEPS.filter((step) => {
      if (!this.band) return step === "age";
      if (step === "weight" && this.band.ask_weight !== "yes") return false;
      if (step === "temp" && this.band.ask_temp === "no") return false;
      if (step === "pref" && this.band.ask_preference !== "yes") return false;
      if (step === "fabric" && !this.fabricAsk) return false;
      return true;
    });
  }

  private seasonLabel() {
    const label = this.querySelector("[data-season-label]") as HTMLElement | null;
    if (!label) return;
    if (this.answers.tempMode !== "season" || !this.rules) {
      label.hidden = true;
      return;
    }
    const expecting = this.band && this.band.id === "expecting" && this.answers.dueMonth;
    const month = expecting ? +this.answers.dueMonth : new Date().getMonth() + 1;
    const row = seasonFor(month);
    if (!row) {
      label.hidden = true;
      return;
    }
    const hemisphere = this.rules.settings.hemisphere || "south";
    const names = (this.rules.season_tog && this.rules.season_tog.month_names) || [];
    const template = expecting ? this.copy.season_preview : this.copy.season_preview_now;
    label.textContent = this.text(template || "", {
      month: names[month - 1] || "",
      season: row["season_" + hemisphere] || "",
      togs: this.joinList(String(row.togs || "").split("|")),
    });
    label.hidden = false;
  }

  private validate() {
    const step = this.steps[this.index];
    let ok = true;
    if (step === "age") {
      ok =
        !!this.answers.ageBand &&
        (!this.band || this.band.ask_rolling !== "yes" || !!this.answers.rolling) &&
        (!this.band || this.band.id !== "expecting" || !!this.answers.dueMonth);
    }
    if (step === "temp") ok = this.answers.tempMode === "season" || (this.answers.tempMode === "temp" && this.tempInRange());
    if (step === "weight") ok = this.answers.weightLb !== null;
    if (step === "pref") ok = !!this.answers.s3type && !!this.answers.sleeves;
    if (step === "fabric") ok = !!this.answers.fabric;
    if (step === "style") ok = this.answers.colors !== null;
    const next = this.querySelector("[data-next]") as HTMLButtonElement | null;
    if (next) {
      next.disabled = !ok;
      next.hidden = !!AUTO[step];
    }
  }

  private autoAdvance() {
    const step = this.steps[this.index];
    const next = this.querySelector("[data-next]") as HTMLButtonElement | null;
    if (!AUTO[step] || (next && next.disabled)) return;
    window.clearTimeout(this.advanceTimer);
    this.advanceTimer = window.setTimeout(() => {
      if (this.index < this.steps.length - 1) this.go(this.index + 1, true);
    }, 260);
  }

  private async onNext() {
    // One advance at a time: a second press while the catalogue loads would skip a step -- EZ 20261028
    if (this.advancing) return;
    const next = this.querySelector("[data-next]") as HTMLButtonElement | null;
    const startIndex = this.index;
    if (this.steps[this.index] === "temp" || this.index >= this.steps.length - 1) {
      this.advancing = true;
      if (next) next.disabled = true;
      try {
        await this.productsReady;
      } finally {
        this.advancing = false;
        this.validate();
      }
      // The answers may have changed while the catalogue loaded (Back pressed, or the field edited to an invalid value) -- EZ 20261028
      if (this.index !== startIndex || (next && next.disabled)) return;
    }
    if (this.steps[this.index] === "temp") this.refreshFabric();
    if (this.index < this.steps.length - 1) {
      this.go(this.index + 1, true);
      return;
    }
    try {
      this.renderResult(recommend(this.answers));
    } catch (error) {
      console.error(error);
      this.showNap();
      this.announce(this.copy.nap || "", true);
    }
  }

  private refreshFabric() {
    const current = this.steps[this.index];
    const was = this.fabricAsk;
    this.fabricAsk = false;
    this.answers.fabric = null;
    this.querySelectorAll('[data-field="fabric"] .sleep-finder__opt').forEach((button) => {
      button.classList.remove("sleep-finder__opt--selected");
      button.setAttribute("aria-pressed", "false");
    });
    try {
      const options = fabricOptions(this.answers);
      if (options.ask) {
        this.fabricAsk = true;
        this.fill(
          '[data-field="fabric"]',
          options.options.map((option) => ({ value: option.id, label: option.label, sub: option.sublabel }))
        );
      }
    } catch (error) {
      console.warn("SleepFinder fabricOptions", error);
    }
    if (this.fabricAsk !== was) {
      this.computeSteps();
      const nextIndex = this.steps.indexOf(current);
      if (nextIndex >= 0) this.index = nextIndex;
    }
  }

  private go(nextIndex: number, moveFocus: boolean) {
    this.index = nextIndex;
    const step = this.steps[this.index];
    this.querySelectorAll(".sleep-finder__step").forEach((node) => {
      (node as HTMLElement).hidden = (node as HTMLElement).dataset.step !== step;
    });
    this.drawProgress();
    const prev = this.querySelector("[data-prev]") as HTMLButtonElement | null;
    if (prev) prev.hidden = this.index === 0;
    const next = this.querySelector("[data-next]") as HTMLButtonElement | null;
    if (next) next.textContent = this.index === this.steps.length - 1 ? this.copy.show_kit || "" : this.copy.next || "";
    this.validate();
    if (moveFocus && this.interacted) this.focusStep();
    this.track("finder_step", { step, index: this.index + 1 });
  }

  private focusStep() {
    const step = this.querySelector(".sleep-finder__step:not([hidden])");
    if (!step) return;
    const headings = step.querySelectorAll("h2");
    for (const heading of Array.from(headings)) {
      if ((heading as HTMLElement).closest("[hidden]")) continue;
      const target = heading as HTMLElement;
      if (!this.selectionSpoken) this.announce((target.textContent || "").trim());
      this.selectionSpoken = false;
      /* Focus waits so the live text is not replaced in the same turn. -- SG 20261026 */
      window.setTimeout(() => target.focus(), 260);
      return;
    }
  }

  private drawProgress() {
    const progress = this.querySelector("[data-progress]");
    if (!progress) return;
    progress.textContent = "";
    this.steps.forEach((_step, index) => {
      const mark = document.createElement("span");
      if (index <= this.index) mark.className = "is-on";
      progress.appendChild(mark);
    });
  }

  private renderResult(result: FinderResult) {
    this.result = result;
    this.querySelectorAll(".sleep-finder__step, .sleep-finder__nav, .sleep-finder__progress").forEach((node) => {
      (node as HTMLElement).hidden = true;
    });
    const kit = this.querySelector("[data-kit]") as HTMLElement | null;
    if (!kit) return;
    kit.hidden = false;
    kit.textContent = "";

    const head = document.createElement("div");
    head.className = "sleep-finder__kit-head";
    const kitTitle = this.element("h2", "sleep-finder__kit-title h3 font-avenir", this.copy.kit_title || "");
    kitTitle.tabIndex = -1;
    head.appendChild(kitTitle);
    const badges = document.createElement("div");
    badges.appendChild(this.element("span", "sleep-finder__badge", result.stage_name));
    if (result.tog && result.tog !== "n/a") badges.appendChild(this.element("span", "sleep-finder__badge", result.tog));
    const sizeLabel = this.text(this.copy.size_badge || "", { size: result.size });
    if (sizeLabel) badges.appendChild(this.element("span", "sleep-finder__badge", sizeLabel));
    head.appendChild(badges);
    kit.appendChild(head);

    const education = result.education_by_group || { hero: result.education };
    for (const group of GROUPS) {
      const items = result.kit.map((line, index) => ({ line, index })).filter((item) => item.line.group === group);
      const notes = education[group] || [];
      if (!items.length && !notes.length) continue;
      const raw = this.copy[GROUP_KEYS[group]] || group;
      const splitAt = raw.indexOf(":");
      const label = splitAt > -1 ? raw.slice(0, splitAt) : raw;
      const sub = splitAt > -1 ? raw.slice(splitAt + 1).trim() : "";
      const heading = document.createElement("div");
      heading.className = "sleep-finder__group label-sm";
      heading.appendChild(document.createTextNode(label));
      if (sub) heading.appendChild(this.element("small", "", sub));
      kit.appendChild(heading);
      for (const note of notes) kit.appendChild(this.element("div", "sleep-finder__edu", note));
      for (const item of items) kit.appendChild(this.kitRow(item.line, item.index));
    }

    const total = document.createElement("div");
    total.className = "sleep-finder__total";
    total.appendChild(this.element("strong", "", this.copy.kit_total || ""));
    const amounts = document.createElement("strong");
    amounts.appendChild(this.element("span", "", ""));
    const amount = amounts.lastElementChild as HTMLElement;
    amount.dataset.total = "";
    total.appendChild(amounts);
    kit.appendChild(total);
    const discounted = this.element("p", "sleep-finder__discounted", "");
    discounted.dataset.discounted = "";
    discounted.hidden = true;
    kit.appendChild(discounted);

    const offerOn =
      this.dataset.offerEnabled === "true" && !!(this.dataset.discountCode && this.dataset.klaviyoCompany);
    if (offerOn) {
      const template = this.querySelector("[data-offer-template]") as HTMLTemplateElement | null;
      if (template) kit.appendChild(template.content.cloneNode(true));
    } else {
      const actions = document.createElement("div");
      actions.className = "sleep-finder__actions";
      const add = document.createElement("button");
      add.type = "button";
      add.className = "sleep-finder__btn";
      add.dataset.add = "";
      add.textContent = this.copy.add_kit || "";
      actions.appendChild(add);
      actions.appendChild(this.ghost("[data-back]", this.copy.back || ""));
      actions.appendChild(this.ghost("[data-restart]", this.copy.restart || ""));
      kit.appendChild(actions);
    }

    const note = this.element("p", "sleep-finder__note", this.copy.added_note || "");
    note.dataset.addedNote = "";
    note.hidden = true;
    kit.appendChild(note);

    if (result.warnings.length) {
      const warnings = document.createElement("div");
      warnings.className = "sleep-finder__warn";
      result.warnings.forEach((warning, index) => {
        if (index) warnings.appendChild(document.createElement("br"));
        warnings.appendChild(document.createTextNode(formatWarning(warning, this.copy)));
      });
      kit.appendChild(warnings);
    }
    if (result.disclaimer) kit.appendChild(this.element("p", "sleep-finder__disclaimer", result.disclaimer));

    this.paintTotal();
    this.announce(this.copy.result_ready || this.copy.kit_title || "", false);
    const title = kit.querySelector("h2") as HTMLElement | null;
    if (title) title.focus();
    this.track("finder_result", {
      stage: result.stage,
      tog: result.tog,
      size: result.size,
      items: result.kit.length,
      total: result.total,
      age_band: result.inputs.ageBand,
      rolling: result.inputs.rolling,
      style: result.inputs.style,
      temp_mode: result.inputs.tempMode,
    });
  }

  private kitRow(line: FinderSlot, index: number): HTMLElement {
    const on = line.slot !== "extra" && line.slot !== "next_stage";
    const row = document.createElement("div");
    row.className = "sleep-finder__item" + (on ? "" : " sleep-finder__item--off");
    row.dataset.item = String(index);

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = on;
    checkbox.dataset.i = String(index);
    checkbox.setAttribute("aria-label", this.text(this.copy.include || "", { title: line.title }));
    const check = document.createElement("label");
    check.className = "sleep-finder__check";
    check.appendChild(checkbox);
    row.appendChild(check);

    const image = document.createElement("img");
    image.className = "sleep-finder__fade";
    image.src = line.image;
    image.alt = line.title;
    image.width = 72;
    image.height = 72;
    image.loading = "lazy";
    row.appendChild(image);

    const body = document.createElement("div");
    body.className = "sleep-finder__item-copy sleep-finder__fade";
    body.appendChild(this.element("p", "sleep-finder__slot", line.reason));
    const title = document.createElement("p");
    title.className = "sleep-finder__title";
    const link = document.createElement("a");
    link.href = line.url;
    link.target = "_blank";
    link.rel = "noopener";
    link.appendChild(document.createTextNode(line.title));
    const tabLabel = (this.copy.opens_new_tab || "").trim();
    if (tabLabel) {
      const tab = document.createElement("span");
      tab.className = "sleep-finder__new-tab";
      tab.textContent = " (" + tabLabel + ")";
      link.appendChild(tab);
    }
    title.appendChild(link);
    body.appendChild(title);
    body.appendChild(this.element("p", "sleep-finder__meta", line.variant_title));
    body.appendChild(this.sizeSelect(line, index));
    row.appendChild(body);

    const qty = document.createElement("div");
    qty.className = "sleep-finder__qty sleep-finder__fade";
    qty.dataset.qty = String(index);
    const decrease = document.createElement("button");
    decrease.type = "button";
    decrease.className = "sleep-finder__qty-btn";
    decrease.dataset.dec = "";
    decrease.setAttribute("aria-label", this.text(this.copy.decrease || "", { title: line.title }));
    decrease.textContent = "-";
    decrease.disabled = line.qty <= 1;
    const count = document.createElement("span");
    count.textContent = String(line.qty);
    const increase = document.createElement("button");
    increase.type = "button";
    increase.className = "sleep-finder__qty-btn";
    increase.dataset.inc = "";
    increase.setAttribute("aria-label", this.text(this.copy.increase || "", { title: line.title }));
    increase.textContent = "+";
    qty.appendChild(decrease);
    qty.appendChild(count);
    qty.appendChild(increase);
    row.appendChild(qty);

    const price = this.element("div", "sleep-finder__price sleep-finder__fade", this.money(line.price * line.qty));
    price.dataset.price = String(index);
    row.appendChild(price);
    return row;
  }

  private sizeSelect(line: FinderSlot, index: number): HTMLSelectElement {
    const select = document.createElement("select");
    select.className = "sleep-finder__size";
    select.dataset.size = String(index);
    select.setAttribute("aria-label", this.text(this.copy.size_for || "", { title: line.title }));
    for (const variant of this.variantsFor(line)) {
      const option = document.createElement("option");
      option.value = variant.id;
      option.textContent = variant.title;
      option.selected = variant.id === String(line.variant_id);
      select.appendChild(option);
    }
    return select;
  }

  private variantsFor(line: FinderSlot): any[] {
    const product = getProducts().find((item) => item.handle === line.handle);
    if (!product) return [];
    return product.variants.filter((variant: any) => variant.available || variant.id === String(line.variant_id));
  }

  private onQty(button: HTMLButtonElement) {
    if (!this.result) return;
    const wrap = button.closest("[data-qty]") as HTMLElement | null;
    if (!wrap) return;
    const index = Number(wrap.dataset.qty);
    const line = this.result.kit[index];
    if (!line) return;
    line.qty = Math.max(1, Math.min(10, line.qty + (button.hasAttribute("data-inc") ? 1 : -1)));
    const count = wrap.querySelector("span");
    if (count) count.textContent = String(line.qty);
    const decrease = wrap.querySelector("[data-dec]") as HTMLButtonElement | null;
    if (decrease) decrease.disabled = line.qty <= 1;
    const price = this.querySelector('[data-price="' + index + '"]');
    if (price) price.textContent = this.money(line.price * line.qty);
    const checkbox = this.querySelector('input[type="checkbox"][data-i="' + index + '"]') as HTMLInputElement | null;
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      checkbox.closest(".sleep-finder__item")?.classList.remove("sleep-finder__item--off");
    }
    this.paintTotal();
    this.announce(this.text(this.copy.quantity_now || "", { title: line.title, qty: String(line.qty) }));
  }

  private async onOffer(form: HTMLFormElement) {
    const input = form.querySelector('input[type="email"]') as HTMLInputElement | null;
    const button = form.querySelector("[data-add-discount]") as HTMLButtonElement | null;
    const error = this.querySelector("[data-email-error]") as HTMLElement | null;
    const email = (input?.value || "").trim();
    if (error) error.hidden = true;
    if (input) input.removeAttribute("aria-invalid");
    if (!isValidEmail(email) || !button || !this.result) {
      if (error) {
        error.textContent = this.copy.email_invalid || "";
        error.hidden = false;
      }
      if (input) input.setAttribute("aria-invalid", "true");
      this.announce(this.copy.email_invalid || "", true);
      return;
    }
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.copy.saving || "";
    const result = this.result;
    const subscribed = await subscribeToKlaviyo(email, {
      companyId: this.dataset.klaviyoCompany || "",
      listId: this.dataset.klaviyoList || "",
      source: "Sleep Finder",
      properties: {
        sleep_finder_stage: result.stage,
        sleep_finder_tog: result.tog,
        sleep_finder_size: result.size,
        sleep_finder_age_band: result.inputs.ageBand,
        sleep_finder_rolling: result.inputs.rolling,
        sleep_finder_style: result.inputs.style,
        sleep_finder_temp_f: result.inputs.tempF,
        sleep_finder_temp_mode: result.inputs.tempMode,
        sleep_finder_due_month: result.inputs.dueMonth,
        sleep_finder_kit_total: result.total,
        sleep_finder_date: new Date().toISOString().slice(0, 10),
        sleep_finder_discount_code: this.dataset.discountCode || "",
      },
    });
    button.textContent = label;
    if (!subscribed.ok) {
      button.disabled = false;
      if (error) {
        error.textContent = this.copy.email_failed || "";
        error.hidden = false;
      }
      if (input) input.setAttribute("aria-invalid", "true");
      this.announce(this.copy.email_failed || "", true);
      return;
    }
    await this.add(true, button);
  }

  private async add(withDiscount: boolean, button: HTMLButtonElement) {
    if (!this.result) return;
    const indexes = Array.from(this.querySelectorAll('[data-kit] input[type="checkbox"]:checked')).map((node) =>
      Number((node as HTMLInputElement).dataset.i)
    );
    const lines = indexes
      .map((index) => this.result && this.result.kit[index])
      .filter((line): line is FinderSlot => !!line)
      .map((line) => ({
        id: parseInt(line.variant_id, 10),
        quantity: line.qty,
        properties: { _sleep_finder_slot: line.slot },
      }));
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.copy.adding || "";
    let outcome: CartAddResult;
    try {
      outcome = await addKitToCart(
        lines,
        [this.result.stage, this.result.tog, this.result.size].join("_"),
        withDiscount ? this.dataset.discountCode || "" : ""
      );
    } catch (error) {
      // A network failure or a non-JSON cart response must not leave the button stuck on "Adding" -- EZ 20261028
      console.error(error);
      outcome = { ok: false, description: "", discountApplied: false };
    }
    if (!outcome.ok) {
      button.disabled = false;
      button.textContent = label;
      this.announce(outcome.description || this.copy.cart_error || "", true);
      return;
    }
    // Every add control stays disabled after a success so neither button can add the kit twice -- EZ 20261028
    this.querySelectorAll("[data-kit] [data-add], [data-kit] [data-add-discount]").forEach((node) => {
      (node as HTMLButtonElement).disabled = true;
    });
    const success = outcome.discountApplied
      ? this.text(this.copy.discount_applied || "", { percent: String(this.percent()) })
      : this.copy.added || "";
    button.textContent = success;
    this.announce(success);
    if (outcome.discountApplied) {
      const discounted = this.querySelector("[data-discounted]") as HTMLElement | null;
      if (discounted) {
        discounted.hidden = false;
        discounted.textContent = this.text(this.copy.discounted_total || "", {
          amount: this.money(this.checkedTotal() * (1 - this.percent() / 100)),
          percent: String(this.percent()),
        });
      }
    }
    const note = this.querySelector("[data-added-note]") as HTMLElement | null;
    if (note) note.hidden = false;
  }

  private backFromResult() {
    const kit = this.querySelector("[data-kit]") as HTMLElement | null;
    if (kit) {
      kit.hidden = true;
      kit.textContent = "";
    }
    this.querySelectorAll(".sleep-finder__nav, .sleep-finder__progress").forEach((node) => {
      (node as HTMLElement).hidden = false;
    });
    this.go(this.steps.length - 1, true);
    this.track("finder_back_from_result", {});
  }

  private paintTotal() {
    const node = this.querySelector("[data-total]");
    if (node) node.textContent = this.money(this.checkedTotal());
  }

  private checkedTotal(): number {
    if (!this.result) return 0;
    let total = 0;
    this.querySelectorAll('[data-kit] input[type="checkbox"]:checked').forEach((node) => {
      const line = this.result && this.result.kit[Number((node as HTMLInputElement).dataset.i)];
      if (line) total += line.price * line.qty;
    });
    return total;
  }

  private percent(): number {
    return parseFloat(this.dataset.discountPercent || "") || 0;
  }

  private money(amount: number): string {
    const cents = Math.round(amount * 100);
    const fixed = (cents / 100).toFixed(2);
    const format = this.dataset.moneyFormat || "{{amount}}";
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, fixed.replace(".", ","))
      .replace(/\{\{\s*amount\s*\}\}/g, fixed);
  }

  private announce(message: string, isError = false) {
    const live = this.querySelector("[data-live]") as HTMLElement | null;
    if (!live) return;
    live.classList.toggle("sleep-finder__live--error", isError);
    live.textContent = message;
  }

  private showNap() {
    const sub = this.querySelector(".sleep-finder__sub");
    if (sub) sub.textContent = this.copy.nap || "";
  }

  private track(name: string, detail: Record<string, unknown>) {
    const target = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    target.dataLayer = target.dataLayer || [];
    target.dataLayer.push({ event: name, ...detail });
  }

  private applyCopyPhrases() {
    if (!this.rules.copy) this.rules.copy = {};
    const phrases: Record<string, string> = {
      list_and: this.copy.list_and,
      layer_item: this.copy.layer_item,
      layer_item_one: this.copy.layer_item_one,
      hero_fallback: this.copy.hero_fallback,
    };
    Object.keys(phrases).forEach((key) => {
      const value = phrases[key];
      if (value && value.toLowerCase().indexOf("translation missing") === -1) this.rules.copy[key] = value;
    });
  }

  private optionName(option: HTMLButtonElement): string {
    // Colour tiles start with an empty aria-hidden swatch, so read the label text node -- SG 20261026
    const label = Array.from(option.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && (node.textContent || "").trim());
    return ((label && label.textContent) || option.textContent || "").trim();
  }

  private nextStepHeading(): HTMLElement | null {
    if (this.index >= this.steps.length - 1) return null;
    const root = this.querySelector('[data-step="' + this.steps[this.index + 1] + '"]');
    if (!root) return null;
    const headings = root.querySelectorAll("h2");
    for (const heading of Array.from(headings)) {
      const owner = (heading as HTMLElement).closest("[hidden]");
      if (owner && owner !== root) continue;
      return heading as HTMLElement;
    }
    return null;
  }

  private willAutoAdvance(): boolean {
    const step = this.steps[this.index];
    const next = this.querySelector("[data-next]") as HTMLButtonElement | null;
    return !!AUTO[step] && !(next && next.disabled);
  }

  /* One live message covers the choice and the next question. Focus follows after it. -- SG 20261026 */
  private finishSelection(option: HTMLButtonElement) {
    const label = this.optionName(option);
    const moving = this.willAutoAdvance();
    const heading = this.pendingFocus || (moving ? this.nextStepHeading() : null);
    const question = heading ? (heading.textContent || "").trim() : "";
    const template = question ? this.copy.selected_step || this.copy.selected || "" : this.copy.selected || "";
    this.announce(this.text(template, { label, question }));
    if (this.pendingFocus) {
      const target = this.pendingFocus;
      this.pendingFocus = null;
      this.selectionSpoken = false;
      window.setTimeout(() => target.focus(), 260);
      return;
    }
    this.selectionSpoken = moving;
  }

  private joinList(items: string[]): string {
    const parts = items.map((item) => item.trim()).filter(Boolean);
    if (parts.length <= 1) return parts[0] || "";
    if (!this.copy.list_and) return parts.join(", ");
    return this.text(this.copy.list_and, { left: parts.slice(0, -1).join(", "), right: parts[parts.length - 1] });
  }

  private text(template: string, vars: Record<string, string>): string {
    return String(template || "").replace(/\{(\w+)\}/g, (_match, key) => (vars[key] != null ? vars[key] : ""));
  }

  private element(tag: string, className: string, text: string): HTMLElement {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = text;
    return node;
  }

  private ghost(marker: string, label: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "sleep-finder__btn sleep-finder__btn--ghost";
    if (marker === "[data-back]") button.dataset.back = "";
    if (marker === "[data-restart]") button.dataset.restart = "";
    button.textContent = label;
    return button;
  }

  private hide(node: Element | null) {
    if (node) (node as HTMLElement).hidden = true;
  }
}

customElements.define("sleep-finder", SleepFinder);
